import { expandGrants } from './expand';
import { logRejections, normalizeGrants } from './normalize';
import type { EffectivePermissions, OrOfAnds, PermissionString, RawGrant } from './types';

/**
 * The whole engine (Addendum A.2): normalize → expand → effective set.
 * Runs once per login/refresh. Everything downstream is a set lookup.
 */
export function computeEffectivePermissions(
  raw: readonly RawGrant[],
  catalog: readonly PermissionString[],
  options: { readonly log?: Pick<Console, 'warn'> | false } = {},
): EffectivePermissions {
  const normalized = normalizeGrants(raw);
  if (options.log !== false) logRejections(normalized, options.log ?? console);
  return { raw, normalized, effective: expandGrants(normalized.grants, catalog) };
}

export interface PermissionChecker {
  /** `can(p)` — the only primitive. O(1). */
  can: (permission: PermissionString) => boolean;
  /** At least one of the listed permissions. */
  canAny: (permissions: readonly PermissionString[]) => boolean;
  /** All of the listed permissions. */
  canAll: (permissions: readonly PermissionString[]) => boolean;
  /** OR-of-AND-groups — same semantics as the legacy `hasPermissions(PermissionType[][])`. */
  canGroups: (groups: OrOfAnds) => boolean;
}

export function createChecker(effective: ReadonlySet<PermissionString>): PermissionChecker {
  const can = (permission: PermissionString): boolean => effective.has(permission);
  return {
    can,
    canAny: (permissions) => permissions.some(can),
    canAll: (permissions) => permissions.every(can),
    // An empty group list means "no permission required" — routes without a declaration are public
    // within the authenticated shell, which keeps the manifest honest about what it actually gates.
    canGroups: (groups) => groups.length === 0 || groups.some((group) => group.every(can)),
  };
}
